import { formatDateTime } from './dateFormat'

export const INVENTORY_MANAGE_DEFAULT_LOCATION = 'DK11'

export function parseInventoryManageSearchParams(
  searchParams,
  defaultLocationCode = INVENTORY_MANAGE_DEFAULT_LOCATION,
) {
  const q = searchParams.get('q') || ''
  let locations
  if (!searchParams.has('locations')) {
    locations = [{ code: defaultLocationCode, name: null }]
  } else {
    const raw = searchParams.get('locations') || ''
    locations = raw
      ? raw
          .split(',')
          .map((code) => code.trim())
          .filter(Boolean)
          .map((code) => ({ code, name: null }))
      : []
  }
  const page = Math.max(0, (parseInt(searchParams.get('page') || '1', 10) || 1) - 1)
  const parsedLimit = parseInt(searchParams.get('limit') || '10', 10) || 10
  const limit = [5, 10, 25, 50].includes(parsedLimit) ? parsedLimit : 10
  return { q, locations, page, limit }
}

export function buildInventoryManageSearchParams({
  q,
  locations,
  page,
  limit,
}) {
  const params = new URLSearchParams()
  const trimmed = (q || '').trim()
  if (trimmed) params.set('q', trimmed)

  const codes = (locations || [])
    .map((entry) => (typeof entry === 'string' ? entry : entry?.code))
    .filter(Boolean)
  params.set('locations', codes.join(','))

  if (page > 0) params.set('page', String(page + 1))
  if (limit !== 10) params.set('limit', String(limit))
  return params
}

export function getInventoryManageReturnPath(searchParams) {
  const query =
    typeof searchParams?.toString === 'function'
      ? searchParams.toString()
      : ''
  return query ? `/inventory/manage?${query}` : '/inventory/manage'
}

/** True when the URL carries any inventory list query key (shared / bookmarked link). */
export function inventoryManageHasExplicitParams(searchParams) {
  return (
    searchParams.has('q') ||
    searchParams.has('locations') ||
    searchParams.has('page') ||
    searchParams.has('limit')
  )
}

/**
 * Map UI list filters → GET /inventory query params.
 * `page` in filters is 0-based (table); API page is 1-based.
 * Omits `search` / `locationCodes` when empty (same as legacy fetch).
 */
export function toInventoryApiParams({ q, locations, page, limit }) {
  const params = {
    page: (Number.isFinite(page) ? page : 0) + 1,
    limit: limit ?? 10,
  }
  const trimmed = (q || '').trim()
  if (trimmed) {
    params.search = trimmed
  }
  const codes = (locations || [])
    .map((entry) => (typeof entry === 'string' ? entry : entry?.code))
    .filter(Boolean)
  if (codes.length > 0) {
    params.locationCodes = codes.join(',')
  }
  return params
}

/** Detail path with durable return list URL (survives refresh / new tab). */
export function buildInventoryDetailPath(id, returnPath) {
  const params = new URLSearchParams()
  if (returnPath) params.set('return', returnPath)
  const query = params.toString()
  return query ? `/inventory/${id}?${query}` : `/inventory/${id}`
}

export function resolveInventoryDetailReturnPath(searchParams, locationState) {
  const fromQuery = searchParams?.get?.('return')
  if (fromQuery && fromQuery.startsWith('/')) return fromQuery
  if (locationState?.returnTo && String(locationState.returnTo).startsWith('/')) {
    return locationState.returnTo
  }
  return null
}

export const AVAILABLE_INVENTORY_CSV_COLUMNS = [
  { id: 'sku', label: 'Article Code' },
  { id: 'product_name', label: 'Product Name' },
  { id: 'brand', label: 'Brand' },
  { id: 'barcode', label: 'Barcode' },
  { id: 'quantity_on_hand', label: 'On Hand' },
  { id: 'selling_price', label: 'Selling Price' },
  { id: 'mrp', label: 'MRP' },
  { id: 'location_id', label: 'Location Id' },
  { id: 'location_code', label: 'Location Code' },
  { id: 'updated_at', label: 'Last Updated' },
  { id: 'reserved_quantity', label: 'Reserved Quantity' },
]

const INVENTORY_CSV_COLUMN_MAP = {
  sku: (item) => item.sku || '',
  product_name: (item) => item.adminProduct?.product_name || '',
  brand: (item) => item.adminProduct?.brand || '',
  barcode: (item) => item.adminProduct?.barcode || '',
  quantity_on_hand: (item) =>
    typeof item.quantity_on_hand === 'number'
      ? Math.floor(item.quantity_on_hand)
      : item.quantity_on_hand ?? 0,
  selling_price: (item) =>
    item.adminProduct?.cost_price ?? item.unit_cost ?? '',
  mrp: (item) => item.adminProduct?.price ?? item.unit_price ?? '',
  location_id: (item) => (item.location_id != null ? item.location_id : ''),
  location_code: (item) => {
    if (item.location_code) return item.location_code
    if (item.location && typeof item.location === 'object') {
      return item.location.code || ''
    }
    return ''
  },
  updated_at: (item) =>
    item.updated_at ? formatDateTime(item.updated_at, '') : '',
  reserved_quantity: (item) => item.reserved_quantity || 0,
}

export const buildInventoryCsvContent = (items, selectedColumns) => {
  const headers = selectedColumns.map((colId) => {
    const col = AVAILABLE_INVENTORY_CSV_COLUMNS.find((c) => c.id === colId)
    return col ? col.label : colId
  })

  const rows = items.map((item) =>
    selectedColumns.map((colId) => {
      const value = INVENTORY_CSV_COLUMN_MAP[colId] ? INVENTORY_CSV_COLUMN_MAP[colId](item) : ''
      return value !== null && value !== undefined ? String(value) : ''
    }),
  )

  return [
    headers.join(','),
    ...rows.map((r) =>
      r
        .map((cell) => `"${String(cell || '').replace(/"/g, '""')}"`)
        .join(','),
    ),
  ].join('\n')
}

export const getStockStatusColor = (item) => {
  const quantity = parseInt(item.quantity_on_hand, 10) || 0
  const minLevel = parseInt(item.minimum_stock_level, 10) || 0
  const maxLevel = parseInt(item.maximum_stock_level, 10) || 0
  const reorderPoint = parseInt(item.reorder_point, 10) || 0

  if (quantity === 0) return 'error'
  if (reorderPoint > 0 && quantity <= reorderPoint) return 'warning'
  if (quantity < minLevel) return 'warning'
  if (maxLevel > 0 && quantity > maxLevel) return 'info'
  return 'success'
}

export const getAvailableQuantity = (quantityOnHand, reservedQuantity) => {
  const onHand = parseInt(quantityOnHand, 10) || 0
  const reserved = parseInt(reservedQuantity, 10) || 0
  return onHand - reserved
}

export const validateInventoryQuantities = (quantityOnHand, reservedQuantity) => {
  const onHand = parseInt(quantityOnHand, 10) || 0
  const reserved = parseInt(reservedQuantity, 10) || 0
  if (reserved > onHand) {
    return 'Reserved quantity cannot exceed quantity on hand. Available quantity cannot be negative.'
  }
  return null
}

export const getStockStatus = (item) => {
  const quantity = parseInt(item.quantity_on_hand, 10) || 0
  const minLevel = parseInt(item.minimum_stock_level, 10) || 0
  const maxLevel = parseInt(item.maximum_stock_level, 10) || 0
  const reorderPoint = parseInt(item.reorder_point, 10) || 0

  if (quantity === 0) return 'Out of Stock'
  if (reorderPoint > 0 && quantity <= reorderPoint) return 'Reorder'
  if (quantity < minLevel) return 'Low Stock'
  if (maxLevel > 0 && quantity > maxLevel) return 'Overstock'
  return 'In Stock'
}

/** Format DB timestamp in the browser's local timezone (date + time). */
export const formatInventoryDateTime = (value) => formatDateTime(value)
